import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, Badge, PageHeader } from '@/components/ui/primitives';
import { useAuth } from '@/auth/AuthContext';
import { ENGINES } from '@/engines/registry';
import { cn } from '@/lib/utils';
import { ChangePasswordPanel } from './settings/ChangePasswordPanel';
import { ConnectionsPanel } from './settings/ConnectionsPanel';
import { AuraVoicePanel } from './settings/AuraVoicePanel';

/* /settings — workspace/admin surface (brief v1 §7). Tabbed skeleton covering
 * the settings IA: workspace + compliance, members/roles, entitlements, API
 * keys, sending identities, and account security. Structural; most forms are
 * placeholders. Security › Change password is live (design-spec v1.1 §7.11). */

const TABS = ['Workspace', 'Members', 'Entitlements', 'Compliance', 'Connections', 'API keys', 'Aura Voice', 'Security'] as const;
type Tab = (typeof TABS)[number];

export function SettingsPage() {
  const { user, activeWorkspace } = useAuth();
  const [params] = useSearchParams();
  const requested = params.get('tab');
  const [tab, setTab] = useState<Tab>(
    (TABS as readonly string[]).includes(requested ?? '') ? (requested as Tab) : 'Workspace',
  );

  return (
    <div>
      <PageHeader title="Settings" subtitle={activeWorkspace?.name} />
      <div className="mb-5 flex gap-1 overflow-x-auto border-b border-border-subtle">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={cn(
              '-mb-px shrink-0 whitespace-nowrap border-b-2 px-3 py-2 text-sm',
              tab === t
                ? 'border-ai font-medium text-text-primary'
                : 'border-transparent text-text-secondary hover:text-text-primary',
            )}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'Workspace' && (
        <Card className="p-5">
          <Row label="Workspace name" value={activeWorkspace?.name ?? '—'} />
          <Row label="Account" value={activeWorkspace?.account_id ?? '—'} />
          <Row label="Your role" value={<Badge tone="ai">{activeWorkspace?.role}</Badge>} />
        </Card>
      )}

      {tab === 'Members' && (
        <Card className="p-5">
          <div className="text-sm text-text-secondary">
            <div className="flex items-center justify-between border-b border-border-subtle py-2">
              <span className="text-text-primary">{user?.name} <span className="text-text-tertiary">· {user?.email}</span></span>
              <Badge tone="ai">{activeWorkspace?.role}</Badge>
            </div>
            <p className="mt-3 text-xs text-text-tertiary">
              Member + role management (owner / operator / readonly) is a greenfield auth surface — invite
              flows land with the auth API.
            </p>
          </div>
        </Card>
      )}

      {tab === 'Entitlements' && (
        <Card className="p-5">
          <div className="grid gap-2 sm:grid-cols-2">
            {ENGINES.map((e) => {
              const on = activeWorkspace?.entitlements.includes(e.key);
              return (
                <div key={e.key} data-engine={e.key} className="flex items-center justify-between rounded-md border border-border-subtle px-3 py-2">
                  <span className="flex items-center gap-2 text-sm text-text-primary">
                    <span className="text-engine" aria-hidden>{e.icon}</span>
                    {e.name}
                  </span>
                  <Badge tone={on ? 'success' : 'neutral'}>{on ? 'enabled' : e.status}</Badge>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {tab === 'Compliance' && (
        <Card className="p-5 text-sm text-text-secondary">
          <p className="mb-2 text-text-primary">Compliance is platform law — surfaced, never bypassable.</p>
          <ul className="list-inside list-disc space-y-1 text-text-secondary">
            <li>Mandatory disclosure block + unsubscribe link in every template.</li>
            <li>Suppression list (unsubscribes / complaints) + permanent suppression.</li>
            <li>Complaint-rate auto-pause + per-workspace caps/quotas.</li>
            <li>Platform-level master pause.</li>
          </ul>
        </Card>
      )}

      {tab === 'API keys' && (
        <Card className="p-5 text-sm text-text-secondary">
          Per-workspace API keys (scoped, HMAC-signed). Key management UI lands with the settings API.
          Secrets are shown once on creation, never stored in plaintext client-side.
        </Card>
      )}

      {tab === 'Connections' && (
        <div className="space-y-4">
          <div className="text-sm text-text-secondary">
            Connect your own WordPress site to BookedCentral. Content is pushed as a review-first draft by
            default; publishing stays double-gated.
          </div>
          <ConnectionsPanel />
        </div>
      )}

      {tab === 'Aura Voice' && (
        <div className="space-y-4">
          <div className="text-sm text-text-secondary">
            Tune how Aura speaks in this workspace. Applies to spoken replies immediately.
          </div>
          <AuraVoicePanel />
        </div>
      )}

      {tab === 'Security' && (
        <div className="space-y-4">
          <div className="text-sm text-text-secondary">
            Signed in as <span className="text-text-primary">{user?.email}</span>.
          </div>
          <ChangePasswordPanel />
        </div>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between border-b border-border-subtle py-2.5 last:border-0">
      <span className="text-sm text-text-secondary">{label}</span>
      <span className="text-sm text-text-primary">{value}</span>
    </div>
  );
}
